import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  Avatar,
  TextField,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Stack,
  Chip,
  Divider,
  alpha,
  useTheme,
} from "@mui/material";
import { AccessTime, Person, Assignment, Notes } from "@mui/icons-material";
import { useQuery, useQueryClient } from "react-query";
import {
  confirmScheduleJob,
  getScheduleTaskDetails,
} from "../../../api/services/scheduling";
import Loader from "../../../components/Loader/Loader";
import { useSnackbar } from "notistack";
import { QK } from "../../../base/qk";
import ExecutionDetails from "./ExecutionDetails";
import MedicationSection from "./MedicationSection";

const ConfirmScheduleJob = ({ JobId, handleClose }) => {
  const theme = useTheme();
  const [notes, setNotes] = useState("");
  const [executionFormData, setExecutionFormData] = useState({});
  const [checklists, setChecklists] = useState([]);
  const [changedChecklists, setChangedChecklists] = useState([]);
  const [medicines, setMedicines] = useState([]);
  const [updatedMedicines, setUpdatedMedicines] = useState([]);

  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const { data: taskDetails, isLoading } = useQuery(
    ["taskDetails", JobId],
    () => getScheduleTaskDetails(JobId),
    {
      enabled: !!JobId,
    }
  );

  useEffect(() => {
    if (taskDetails?.execution_details) {
      const initialData = {};
      const today = new Date().toISOString().split('T')[0];
      const jobDate = taskDetails?.build_for || today;
      
      taskDetails.execution_details.forEach((emp) => {
        const startValue = emp.actual_start 
          ? emp.actual_start.substring(0, 16) 
          : `${jobDate}T${taskDetails?.scheduled_start?.substring(0, 5)}`;
        
        const endValue = emp.actual_end 
          ? emp.actual_end.substring(0, 16) 
          : `${jobDate}T${taskDetails?.scheduled_end?.substring(0, 5)}`;

        initialData[emp.id] = {
          actual_start: startValue,
          actual_end: endValue,
          execution_id: emp.execution_id || null,
        };
      });
      setExecutionFormData(initialData);
    }
    if (taskDetails?.checklists) {
      setChecklists(taskDetails.checklists);
    }
    if (taskDetails?.medicines) {
      setMedicines(taskDetails.medicines);
    }
  }, [taskDetails]);

  const handleChange = (employeeId, field, value) => {
    setExecutionFormData((prevData) => ({
      ...prevData,
      [employeeId]: {
        ...prevData[employeeId],
        [field]: value,
      },
    }));
  };

  const handleChecklistToggle = (index) => {
    const updated = [...checklists];
    updated[index].is_done = !updated[index].is_done;
    setChecklists(updated);

    const changed = [...changedChecklists];
    const existingIndex = changed.findIndex((c) => c.id === updated[index].id);

    if (existingIndex !== -1) {
      changed[existingIndex] = { ...updated[index] };
    } else {
      changed.push({ ...updated[index] });
    }

    setChangedChecklists(changed);
  };

  const validateChecklists = () => {
    if (checklists.length === 0) return true;
    const allChecked = checklists.every((item) => item.is_done);
    if (!allChecked) {
      enqueueSnackbar("Please complete all checklist items before confirming", {
        variant: "warning",
      });
      return false;
    }
    return true;
  };

  const handleMedicinesChange = (updatedList) => {
    setUpdatedMedicines(updatedList);
  };

  const handleSubmit = async () => {
    const task = taskDetails || {};
    if ((!task.completed_notes || task.completed_notes.length === 0) && notes.trim() === "") {
      enqueueSnackbar("Please add notes since there are no completed notes.", {
        variant: "warning",
      });
      return;
    }

    const incompleteExecutions = Object.entries(executionFormData).some(
      ([_, detail]) => !detail.actual_start || !detail.actual_end
    );

    if (incompleteExecutions) {
      enqueueSnackbar("Please fill in all execution start and end times.", {
        variant: "warning",
      });
      return;
    }
    
    if (!validateChecklists()) return;

    const formattedExecutionData = {};
    Object.entries(executionFormData).forEach(([id, data]) => {
      const ensureSeconds = (dateTimeStr) => {
        if (!dateTimeStr) return "";
        let cleanStr = dateTimeStr.replace("T", " ");
        const colonCount = (cleanStr.match(/:/g) || []).length;
        return colonCount < 2 ? `${cleanStr}:00` : cleanStr;
      };

      formattedExecutionData[id] = {
        ...data,
        actual_start: ensureSeconds(data.actual_start),
        actual_end: ensureSeconds(data.actual_end),
      };
    });

    const payload = {
      execution_details: formattedExecutionData,
      handover_notes: notes,
      updated_checklists: changedChecklists.map(({ id, is_done }) => ({ id, is_done })),
      updated_medicines: updatedMedicines,
    };

    const response = await confirmScheduleJob(JobId, payload);
    if (response.status === 200) {
      enqueueSnackbar("Schedule confirmed successfully", { variant: "success" });
      queryClient.invalidateQueries(["taskDetails", JobId]);
      queryClient.invalidateQueries([QK.scheduleTimeline]);
      queryClient.invalidateQueries([QK.notStartedJobs]);
      queryClient.invalidateQueries([QK.needsAttentionJobs]);
      queryClient.invalidateQueries([QK.dailyJobs]);
      handleClose();
    } else {
      enqueueSnackbar("Failed to confirm schedule", { variant: "error" });
    }
  };

  if (isLoading) return <Loader />;

  const task = taskDetails || {};

  return (
    <Box sx={{ width: "100%", maxWidth: 900, p: { xs: 2, sm: 3 }, mx: "auto" }}>
      <Box>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 2, color: theme.palette.text.primary }}>
          {task.visit_name || "Visit Details"}
        </Typography>
        
        <Stack 
          direction={{ xs: "column", sm: "row" }} 
          justifyContent="space-between" 
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <Avatar 
              sx={{ 
                width: 48, 
                height: 48, 
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
              }}
            >
              {task.client_name?.charAt(0) || <Person />}
            </Avatar>
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>
                {task.client_name || "Unknown Client"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                DOB: {task.client_dob ? task.client_dob.split('-').reverse().join('-') : "N/A"}
              </Typography>
            </Box>
          </Stack>

          <Chip
            icon={<AccessTime sx={{ fontSize: 16 }} />}
            label={`Scheduled: ${task.scheduled_start?.substring(0, 5)} - ${task.scheduled_end?.substring(0, 5)}`}
            sx={{
              fontWeight: 600,
              bgcolor: alpha(theme.palette.primary.main, 0.06),
              color: theme.palette.primary.main,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
              px: 1,
              py: 0.5,
            }}
          />
        </Stack>

        {task.execution_details?.length > 0 && (
          <ExecutionDetails
            executionDetails={task.execution_details}
            executionFormData={executionFormData}
            handleChange={handleChange}
          />
        )}

        {task.completed_notes?.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, color: theme.palette.text.secondary }}>
              Completed Notes
            </Typography>
            <Stack spacing={1}>
              {task.completed_notes.map((note, index) => (
                <Box 
                  key={index}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.03),
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                    <strong>{note.candidate_name || "Admin"}:</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {note.notes}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>
        )}

        <TextField
          label="Handover Notes"
          multiline
          rows={3}
          fullWidth
          margin="normal"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          sx={{
            mt: 3,
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
            },
            "& .MuiInputLabel-root": {
              fontWeight: 500,
            },
          }}
        />

        {checklists?.length > 0 && (
          <Card 
            variant="outlined" 
            sx={{ 
              mb: 3, 
              p: 2.5, 
              mt: 3,
              borderRadius: 2,
              borderColor: alpha(theme.palette.divider, 0.1),
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <Assignment sx={{ fontSize: 20, color: theme.palette.primary.main }} />
              <Typography variant="subtitle2" fontWeight={700}>
                Checklist
              </Typography>
            </Stack>
            <FormGroup>
              {checklists.map((item, index) => (
                <FormControlLabel
                  key={item.id}
                  control={
                    <Checkbox 
                      checked={item.is_done} 
                      onChange={() => handleChecklistToggle(index)}
                      sx={{
                        "&.Mui-checked": {
                          color: theme.palette.primary.main,
                        },
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontWeight: item.is_done ? 600 : 400 }}>
                      {item.checklist_name}
                    </Typography>
                  }
                  sx={{
                    py: 0.5,
                    "& .MuiFormControlLabel-label": {
                      width: "100%",
                    },
                  }}
                />
              ))}
            </FormGroup>
          </Card>
        )}

        {medicines?.length > 0 && (
          <MedicationSection
            medicines={medicines}
            handleMedicinesChange={handleMedicinesChange}
          />
        )}
      </Box>

      <Divider sx={{ my: 3 }} />

      <Stack 
        direction="row" 
        justifyContent="flex-end" 
        spacing={2}
      >
        <Button 
          variant="outlined" 
          onClick={handleClose}
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 600,
            px: 3,
          }}
        >
          Cancel
        </Button>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={handleSubmit}
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 600,
            px: 4,
            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
            "&:hover": {
              boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`,
            },
          }}
        >
          Done
        </Button>
      </Stack>
    </Box>
  );
};

export default ConfirmScheduleJob;